/**
 * Channel picker form field for assigning content to a channel.
 *
 * Wraps `DropdownField` in a `FormField` and handles the edge states
 * around a plain dropdown: a loading skeleton while channels resolve, a
 * banner when the user has no channels available, and, if the value
 * already points at a channel the user can't otherwise see, forcing that
 * channel into the option list and disabling the field.
 */
import { useTheme } from '@repo/theme';
import { Banner } from 'component/banner';
import { HorizBox } from 'component/basics';
import type { DropdownFieldOption } from 'component/form';
import { DropdownField, FormField } from 'component/form';
import { PopoverTip } from 'component/popup/popover-tip';
import { TextContainerSkeleton } from 'component/progress/text-container-skeleton';
import { UtilityText } from 'component/text';
import { useAllChannels } from 'data/channels';
import { t } from 'util/stdtypes';

import { useMyChannels } from './channel-dropdown';

type ChannelFormFieldProps = {
    onChange: (channelKey: string | null) => void;
    value: string | null;
};

export function ChannelFormField({ onChange, value }: ChannelFormFieldProps) {
    const { colors } = useTheme();
    const userChannels = useMyChannels();
    const userChannelArray = Object.values(userChannels || {});
    const { channels: allChannels } = useAllChannels();

    const options: DropdownFieldOption[] = userChannelArray.map((channel) => {
        return {
            text: channel.name,
            key: channel.channelKey,
        };
    });

    if (null === userChannels) {
        return (
            <FormField label={t('channels.title', 'Channel')}>
                <TextContainerSkeleton height={48} width='100%' />
            </FormField>
        );
    }

    // If the space is assigned to a channel the user can't access, show it but
    // disable the dropdown so they can't reassign it — regardless of whether
    // they have channels of their own.
    let disabled = false;
    const assignedButInaccessible = value && !userChannelArray.some((channel) => channel.channelKey === value);
    const inaccessibleChannel = assignedButInaccessible ? allChannels.find((channel) => channel.channelKey === value) : undefined;
    if (inaccessibleChannel) {
        disabled = true;
        options.unshift({
            text: inaccessibleChannel.name,
            key: inaccessibleChannel.channelKey,
        });
    }

    if (options.length === 0) {
        return (
            <FormField label={t('channels.title', 'Channel')}>
                <Banner color={colors.colorSurfaceSecondary}>
                    <HorizBox gap={4}>
                        <UtilityText label={t('channels.no_channels_available', 'No channels available')} />
                        <PopoverTip
                            label={t('channels.no_channels_available_info', 'Create and manage channels in the moderation dashboard.')}
                        />
                    </HorizBox>
                </Banner>
            </FormField>
        );
    }

    if (value) {
        options.unshift({
            label: t('channels.no_channel', 'No channel'),
            key: 'no-channel',
        });
    }
    return (
        <FormField label={t('channels.title', 'Channel')}>
            <DropdownField
                disabled={disabled}
                onChange={(value) => onChange(value === 'no-channel' ? null : value)}
                options={options}
                placeholder={t('channels.select_channel', 'Select a channel')}
                value={value}
            />
        </FormField>
    );
}
